// src/pages/ChatbotSettings/CreateChatbotName.js
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { 
    TextField, 
    Typography, 
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    Card,
    CardContent
} from '@mui/material'; 
import axiosInstance from '../../../utils/axiosInstance';

import { useNavigate, useParams } from 'react-router-dom';

const CreateChatbotName = forwardRef(({ onComplete, onError }, ref) => {
    const [chatbotName, setChatbotName] = useState('');
    const [chatbotType, setChatbotType] = useState('');
    const [nameError, setNameError] = useState('');
    const [typeError, setTypeError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    const navigate = useNavigate();

    // Define chatbot types
    const chatbotTypes = [
        { value: 'personal', label: 'Personal' },
        { value: 'business', label: 'Business' }
    ];

    useEffect(() => {
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userData = JSON.parse(userStr);
                if (userData.llm_settings && userData.llm_settings.chatbot_name) {
                    setChatbotName(userData.llm_settings.chatbot_name);
                }
                if (userData.llm_settings && userData.llm_settings.chatbot_type) {
                    setChatbotType(userData.llm_settings.chatbot_type);
                }
            }
        } catch (e) {
            console.error("Error reading initial chatbot data:", e);
        }
    }, []);

    const validateName = (name) => {
        if (!name.trim()) {
            return "Chatbot name cannot be empty.";
        }
        if (name.trim().length > 30) {
            return "Chatbot name must be 30 characters or less.";
        }
        return "";
    };

    const validateType = (type) => {
        if (!type) {
            return "Please select a chatbot type.";
        }
        return "";
    };

    const handleSave = async () => {
        const nameErrorMsg = validateName(chatbotName);
        const typeErrorMsg = validateType(chatbotType);
        
        if (nameErrorMsg || typeErrorMsg) {
            setNameError(nameErrorMsg);
            setTypeError(typeErrorMsg);
            const errorMessage = nameErrorMsg || typeErrorMsg;
            if (onError) onError(errorMessage);
            return false;
        }
        
        setNameError('');
        setTypeError('');
        setIsSaving(true);

        try {
            await axiosInstance.post(`/chatbots`, {
                chatbot_name: chatbotName.trim(),
                chatbot_type: chatbotType
            });
            
            if (onComplete) onComplete({ nextSlug: 'set-avatar' });
            return true;
        } catch (error) {
            const message = error.response?.data?.message || error.message || "Error saving chatbot data.";
            console.error("Save chatbot error:", message);
            setNameError(message);
            if (onError) onError(message);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({
        triggerSave: handleSave
    }));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2, width: '100%' }}>
            <Typography variant="h6" color="textPrimary">Setup Your Chatbot</Typography>
            <Typography variant="body2" color="textPrimary" sx={{ textAlign: 'center', mb: 1 }}>
                Give your chatbot a name and select its type to get started.
            </Typography>
            
            {/* Chatbot Name Field */}
            <TextField
                fullWidth
                label="Chatbot Name"
                value={chatbotName}
                onChange={(e) => {
                    setChatbotName(e.target.value);
                    if (nameError) setNameError('');
                }}
                disabled={isSaving}
                error={!!nameError}
                helperText={nameError}
                inputProps={{ maxLength: 30 }}
                sx={{
                    '& .MuiInputBase-root': {
                        border: 'none',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                }}
            />

            {/* Chatbot Type Selection */}
            <Typography variant="body2" color="textPrimary" sx={{ textAlign: 'center', mb: 1 }}>
                Select a chatbot type: Personal or Business.
            </Typography>
            <RadioGroup
                row
                value={chatbotType}
                onChange={(e) => {
                    setChatbotType(e.target.value);
                    if (typeError) setTypeError('');
                }}
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: 'center',
                }}
            >
                {chatbotTypes.map((type) => (
                    <Card
                        key={type.value}
                        variant="outlined"
                        sx={{
                            flex: { xs: '1 0 100%', sm: '1 0 45%' },
                            borderRadius: 2,
                            border: chatbotType === type.value ? '2px solid' : '1px solid',
                            borderColor: chatbotType === type.value ? 'primary.main' : 'grey.300',
                            backgroundColor: chatbotType === type.value ? 'primary.light' : 'background.paper',
                            boxShadow: chatbotType === type.value ? 3 : 1,
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                boxShadow: 4,
                                backgroundColor: 'action.hover',
                                cursor: 'pointer',
                            },
                            '&:active': {
                                boxShadow: 1,
                            },
                        }}
                        onClick={() => setChatbotType(type.value)}
                    >
                        <FormControlLabel
                            value={type.value}
                            control={
                                <Radio
                                    checked={chatbotType === type.value}
                                    sx={{
                                        color: chatbotType === type.value ? 'primary.main' : 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'primary.main',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: chatbotType === type.value ? 600 : 400,
                                        color: chatbotType === type.value ? 'primary.main' : 'text.primary',
                                    }}
                                >
                                    {type.label}
                                </Typography>
                            }
                            sx={{
                                width: '100%',
                                m: 0,
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                            }}
                        />
                    </Card>
                ))}
            </RadioGroup>
            {typeError && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {typeError}
                </Typography>
            )}
        </Box>
    );
});

export default CreateChatbotName;